import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

/**
 * Resolve a Formik error key to the element the member should be looking at.
 *
 * `[data-error-anchor]` comes first so a field can nominate a stand-in: the address input
 * lives inside `<gmp-place-autocomplete>`'s shadow root and is not reachable from here, and
 * the consent checkbox is a 0x0 `opacity: 0` input that would scroll to the wrong place.
 */
const resolveField = (key: string): Element | null =>
  document.querySelector(`[data-error-anchor="${key}"]`) ??
  document.querySelector(`[name="${key}"]`);

/**
 * The errored field that comes first in the document, which is not the same as the first key
 * Formik hands us — `errors` is in Yup's schema order, so picking `keys[0]` can scroll past a
 * bad field that is higher up the page.
 */
const topmostErrorField = (keys: string[]): Element | null =>
  keys.reduce<Element | null>((topmost, key) => {
    const candidate = resolveField(key);
    if (!candidate) return topmost;
    if (!topmost) return candidate;

    const candidateIsEarlier =
      // eslint-disable-next-line no-bitwise
      topmost.compareDocumentPosition(candidate) &
      Node.DOCUMENT_POSITION_PRECEDING;

    return candidateIsEarlier ? candidate : topmost;
  }, null);

/** `offsetParent` is null for a hidden element — focusing it would scroll nowhere useful. */
const isVisible = (element: Element): element is HTMLElement =>
  element instanceof HTMLElement && element.offsetParent !== null;

/**
 * Scrolls the first invalid field into view after a failed submit.
 *
 * Every step of the flow scrolls inside `FlowScreen`'s fixed overlay rather than the window,
 * so this has to be `scrollIntoView` — `window.scrollTo` is a no-op there.
 */
const ErrorFocus = () => {
  const { isSubmitting, isValidating, errors } = useFormikContext();
  const wasSubmitting = useRef(false);

  useEffect(() => {
    const submitJustSettled = wasSubmitting.current && !isSubmitting;
    wasSubmitting.current = isSubmitting;

    if (!submitJustSettled || isValidating) return undefined;

    const keys = Object.keys(errors);
    if (keys.length === 0) return undefined;

    // One frame is enough for the error messages to paint, which is what shifts the fields
    // into their final position — scrolling before that lands slightly off.
    const frame = requestAnimationFrame(() => {
      const field = topmostErrorField(keys);
      if (!field) return;

      field.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // `preventScroll` so focus does not jump the container out from under the smooth scroll.
      if (isVisible(field)) field.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frame);
  }, [isSubmitting, isValidating, errors]);

  return null;
};

export default ErrorFocus;
